"use client";

import * as React from "react";
import { cn } from "@marketing-sites/utils/cn";
import { getAnalyticsRegion } from "@marketing-sites/analytics/get-analytics-region";
import { triggerAnalyticsClick } from "@marketing-sites/analytics/trigger-analytics-event";
import { TrackingType } from "@marketing-sites/analytics/types";
import { useCarousel } from "./carousel.context";
import { useAutoplayProgress } from "./carousel.hooks";
import type {
  AutoplayProgressProps,
  ThumbnailProgressRenderProps,
  ThumbnailProps,
} from "./carousel.types";

const DEFAULT_ANIMATION_NAME = "carouselAutoplay";
const DEFAULT_KEYFRAMES = `@keyframes ${DEFAULT_ANIMATION_NAME} { from { transform: scaleX(0); } to { transform: scaleX(1); } }`;

/**
 * Render-prop component for the autoplay progress animation.
 * A default `scaleX(0 → 1)` keyframe (`"carouselAutoplay"`) is injected automatically.
 */
export function AutoplayProgress({
  animationName = DEFAULT_ANIMATION_NAME,
  children,
}: AutoplayProgressProps) {
  const { emblaApi } = useCarousel();
  const progressRef = React.useRef<HTMLElement | null>(null);
  const refCallback: React.RefCallback<HTMLElement> = React.useCallback(
    (node) => {
      progressRef.current = node;
    },
    [],
  );
  const { showAutoplayProgress } = useAutoplayProgress(emblaApi, progressRef);
  return (
    <>
      {animationName === DEFAULT_ANIMATION_NAME && (
        <style>{DEFAULT_KEYFRAMES}</style>
      )}
      {children({
        ref: refCallback,
        isPlaying: showAutoplayProgress,
        animationName,
      })}
    </>
  );
}

/** Flex wrapper for thumbnail buttons. Defaults to a horizontal row. */
export function Thumbnails({
  className,
  ...rest
}: React.ComponentProps<"div">) {
  return <div className={cn("flex gap-2", className)} {...rest} />;
}

const defaultProgress = ({
  ref,
  isPlaying,
  animationName,
  isSelected,
}: ThumbnailProgressRenderProps) => (
  <div
    ref={ref}
    className={cn(
      "absolute bottom-0 left-0 h-0.5 w-full origin-left bg-current",
      isPlaying && isSelected ? "opacity-100" : "opacity-0",
    )}
    style={{ animation: `${animationName} linear` }}
  />
);

/**
 * A thumbnail button that scrolls to its slide on click.
 * Sets `data-selected` when active; use for CSS styling: `data-selected:opacity-100`.
 */
export function Thumbnail({
  index,
  analyticsId,
  className,
  progress = defaultProgress,
  children,
  "aria-label": ariaLabel,
  ...rest
}: ThumbnailProps) {
  const { emblaApi, setHoveredIndex } = useCarousel();
  const [isSelected, setIsSelected] = React.useState(false);

  React.useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () =>
      setIsSelected(emblaApi.selectedScrollSnap() === index);
    onSelect();
    emblaApi.on("select", onSelect).on("reInit", onSelect);
  }, [emblaApi, index]);

  const handleMouseEnter = React.useCallback(
    () => setHoveredIndex(index),
    [setHoveredIndex, index],
  );
  const handleMouseLeave = React.useCallback(
    () => setHoveredIndex(null),
    [setHoveredIndex],
  );

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!emblaApi) return;

      if (!isSelected) {
        const location = getAnalyticsRegion(event.currentTarget) ?? "Unknown";
        const raw =
          ariaLabel ??
          event.currentTarget.innerText?.replace(/\s+/g, " ").trim() ??
          `Slide ${index + 1}`;
        const text = raw.length > 25 ? `${raw.slice(0, 25).trim()}…` : raw;
        triggerAnalyticsClick({
          text: `Carousel: ${text}`,
          location,
          type: TrackingType.Button,
          analyticsId,
        });

        emblaApi.scrollTo(index);
        emblaApi.plugins()?.autoplay?.reset();
      }
    },
    [emblaApi, index, ariaLabel, isSelected, analyticsId],
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-selected={isSelected || undefined}
      aria-label={ariaLabel}
      className={cn(
        "relative overflow-hidden focus-visible:outline-2 focus-visible:outline-offset-2",
        className,
      )}
      {...rest}
    >
      {children}
      <AutoplayProgress>
        {({ ref, isPlaying, animationName }) =>
          progress({ ref, isPlaying, animationName, isSelected })
        }
      </AutoplayProgress>
    </button>
  );
}
